import _ from 'lodash';
import sumsumApi from '@/api/SumsumApi';
import dailyRoutineApi from '@/api/DailyRoutineApi';
import moment from 'moment';
import { isViewerCard } from '@/daily-routine/daily-routine-actions';

const INTRO_DAILY_ROUTINE_DISMISSED_KEY = 'riseup.introDailyRoutineDismissed';
const DAILY_ROUTINE_OPENED_DATE = 'riseup.dailyRoutineOpenedDate';
const MAX_INITIAL_CARDS = 8;

const state = {
  dailyRoutineLoading: true,
  dailyRoutines: [],
  dailyRoutinesNextChunk: [],
  showDailyRoutineViewer: false,
  specialCards: {},
  isIntroDismissed: false,
  shouldDisplayEntryCountCard: false,
};

const UNIQUE_CARDS = [
  'INTRO',
  'DONE',
  'DONE_WITH_MORE_CARDS',
  'ENTRY_COUNT',
];

const getters = {
  doneCard: state => (state.dailyRoutinesNextChunk.length > 0 ? state.specialCards.DONE_WITH_MORE_CARDS : state.specialCards.DONE),
  availableCardsLeft: state => state.dailyRoutines.length > 0 || state.dailyRoutinesNextChunk.length > 0,
};

function todayDateString() {
  return moment().format('YYYY-MM-DD');
}

function formatDateString(date) {
  return moment(date).format('YYYY-MM-DD');
}

function isDailyRoutineOpenedToday() {
  const openedDate = localStorage.getItem(DAILY_ROUTINE_OPENED_DATE);
  if (!openedDate) {
    return false;
  }
  return formatDateString(openedDate) === todayDateString();
}

const actions = {
  async fetchDailyRoutine({ commit, state, rootGetters }) {
    commit('setDailyRoutineLoading', true);
    const dailyRoutines = await sumsumApi.getDailyRoutines();
    const { customerDailyRoutines, specialCards } = _.reduce(dailyRoutines, (result, card) => {
      if (UNIQUE_CARDS.includes(card.type)) {
        result.specialCards[card.type] = card;
      } else if (isViewerCard(card)) {
        result.customerDailyRoutines.push(card);
      }
      return result;
    }, { customerDailyRoutines: [], specialCards: {} });

    const { sequenceDays } = await dailyRoutineApi.getDailyRoutineStreak();
    commit('setShouldDisplayEntryCountCard', sequenceDays > 0 && !isDailyRoutineOpenedToday());

    commit('setSpecialCards', specialCards);
    const isIntroDismissed = localStorage.getItem(INTRO_DAILY_ROUTINE_DISMISSED_KEY) === 'true';
    commit('setIsIntroDismissed', isIntroDismissed);

    const firstChunk = customerDailyRoutines.slice(0, MAX_INITIAL_CARDS);
    commit('setDailyRoutines', firstChunk);
    commit('setDailyRoutinesNextChunk', _.drop(customerDailyRoutines, MAX_INITIAL_CARDS));
    commit('setDailyRoutineLoading', false);
  },

  async fetchDailyRoutinesNextChunk({ commit, state }) {
    commit('setDailyRoutines', state.dailyRoutinesNextChunk);
    commit('setDailyRoutinesNextChunk', []);
  },

  async dismissDailyRoutine({ commit, state }, entityId) {
    await dailyRoutineApi.markDailyRoutineAsRead(entityId);
    const updateDrs = _.reject(state.dailyRoutines, dr => dr.entityId === entityId);
    commit('setDailyRoutines', updateDrs);
  },

  async dismissDailyRoutineFromNextChunk({ commit, state }, entityId) {
    await dailyRoutineApi.markDailyRoutineAsRead(entityId);
    const updateDrs = _.reject(state.dailyRoutinesNextChunk, dr => dr.entityId === entityId);
    commit('setDailyRoutinesNextChunk', updateDrs);
  },

  dismissIntro({ commit, state }) {
    commit('setIsIntroDismissed', true);
    localStorage.setItem(INTRO_DAILY_ROUTINE_DISMISSED_KEY, 'true');
  },

  dismissDailyRoutineByTransactionId({ state, dispatch }, transactionId) {
    const dailyRoutineToDismiss = _.find(state.dailyRoutines, dr => dr.transactionId === transactionId);
    const dailyRoutineFromNextChunkToDismiss = _.find(state.dailyRoutinesNextChunk, dr => dr.transactionId === transactionId);
    if (dailyRoutineToDismiss) {
      dispatch('dismissDailyRoutine', dailyRoutineToDismiss.entityId);
    }
    if (dailyRoutineFromNextChunkToDismiss) {
      dispatch('dismissDailyRoutineFromNextChunk', dailyRoutineFromNextChunkToDismiss.entityId);
    }
  },
};

const mutations = {
  setDailyRoutineLoading(state, dailyRoutineLoading) {
    state.dailyRoutineLoading = dailyRoutineLoading;
  },
  setDailyRoutines(state, dailyRoutines) {
    state.dailyRoutines = dailyRoutines;
  },
  setDailyRoutinesNextChunk(state, dailyRoutines) {
    state.dailyRoutinesNextChunk = dailyRoutines;
  },
  setShowDailyRoutineViewer(state, showDailyRoutineViewer) {
    state.showDailyRoutineViewer = showDailyRoutineViewer;
  },
  setIsIntroDismissed(state, isIntroDismissed) {
    state.isIntroDismissed = isIntroDismissed;
  },
  setSpecialCards(state, specialCards) {
    state.specialCards = specialCards;
  },
  setShouldDisplayEntryCountCard(state, shouldDisplayEntryCountCard) {
    if (!shouldDisplayEntryCountCard) {
      localStorage.setItem(DAILY_ROUTINE_OPENED_DATE, todayDateString());
    }
    state.shouldDisplayEntryCountCard = shouldDisplayEntryCountCard;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
